import {
    Component,
    InputSignal,
    EventEmitter,
    Output,
    WritableSignal,
    input,
    signal,
    Signal,
    computed,
    effect,
    inject,
} from '@angular/core';
import { isNumber } from 'lodash';
import { CommonModule } from '@angular/common';
import {
    ColumnKey,
    HeaderCol,
    HeaderRow,
    Row,
    setUpRow,
    sortRows,
} from '../models';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import { MatIconModule } from '@angular/material/icon';
import { z } from 'zod';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';

enum Statuses {
    Loading = 'LOADING',
    None = 'NONE',
    Error = 'ERROR',
    Loaded = 'LOADED',
}
const StatusesSchema = z.nativeEnum(Statuses);

const StatusPayloadSchema = z.object({
    id: z.string(),
    status: StatusesSchema,
    emptyRowNumbers: z.number().optional(),
});

@Component({
    selector: 'iq-table-rate-shared',
    standalone: true,
    imports: [CommonModule, DatiqTemplateModule, MatIconModule],
    templateUrl: './iq-table-rate-shared.component.html',
    styleUrl: './iq-table-rate-shared.component.scss',
})
export class IqTableRateSharedComponent {
    id: InputSignal<string> = input('');
    headerRow: InputSignal<HeaderRow> = input(null);
    rows: InputSignal<Row[]> = input([]);
    payloadRows: WritableSignal<Row[]> = signal([]);
    numOfRows: InputSignal<number> = input(0);
    status: WritableSignal<Statuses> = signal(Statuses.Loading);
    emptyRowNumbers: WritableSignal<number> = signal(0);
    currentRows: Signal<Row[]> = computed(() => {
        if (this.rows()?.length) return this.rows();

        return [...this.payloadRows()];
    });
    sortedRows: Signal<Row[]> = computed(() => {
        const headerRow = this.currentHeaderRow();
        const rows = this.currentRows();

        return sortRows(rows, headerRow);
    });
    activeHeaderCol: WritableSignal<{ colKey: ColumnKey; col: HeaderCol }> =
        signal({
            colKey: 'date',
            col: { id: 'date', label: 'Date', sort: 'none' },
        });

    currentHeaderRow: Signal<HeaderRow> = computed(() => {
        if (this.headerRow()) return this.headerRow();

        const r = setUpRow(this.activeHeaderCol(), this.id());
        return r;
    });

    colKeys: WritableSignal<ColumnKey[]> = signal([
        'date',
        'low',
        'average',
        'high',
    ]);

    isLoaded: Signal<boolean> = computed(() => {
        return this.status() === Statuses.Loaded;
    });
    isNone: Signal<boolean> = computed(() => {
        return this.status() === Statuses.None;
    });
    isError: Signal<boolean> = computed(() => {
        return this.status() === Statuses.Error;
    });
    isLoading: Signal<boolean> = computed(() => {
        return this.status() === Statuses.Loading;
    });

    emptyRows: Signal<Row[]> = computed(() => {
        const numOfEmptyRows = isNumber(this.emptyRowNumbers())
            ? this.emptyRowNumbers()
            : this.numOfRows();

        return [...new Array(numOfEmptyRows)].map(
            (x: never, index: number): Row => {
                return {
                    date: {
                        id: `date-${index}`,
                        label: '',
                    },
                    high: {
                        id: `high-${index}`,
                        label: '',
                    },
                    average: {
                        id: `average-${index}`,
                        label: '',
                    },
                    low: {
                        id: `low-${index}`,
                        label: '',
                    },
                };
            },
        );
    });

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    @Output()
    loaded: EventEmitter<Row[]> = new EventEmitter<Row[]>();

    @Output()
    headerClicked: EventEmitter<{ colKey: ColumnKey; col: HeaderCol }> =
        new EventEmitter<{ colKey: ColumnKey; col: HeaderCol }>();
    private _dispatchActions: Record<string, IqEventBusBrokerHandler>;

    constructor() {
        effect(() => {
            const isLoaded = this.isLoaded();

            if (isLoaded) {
                this.load();
            }
        });

        //#region Register ACtions
        this._dispatchActions = this._eventBus.registerMap({
            'type:rate-table-status-update': {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;

                    const test = StatusPayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'type:table-rate-status-update Invalid Payload',
                        );
                        return;
                    }

                    this.status.set(payload.status);

                    const statusUpdateMap: Record<Statuses, () => void> = {
                        [Statuses.Loading]: () => {
                            this.emptyRowNumbers.set(payload.emptyRowNumbers);
                        },
                        [Statuses.None]: () => {},
                        [Statuses.Error]: () => {},
                        [Statuses.Loaded]: () => {},
                    };
                    statusUpdateMap[payload.status]();
                },
            },
            updateTableRateView: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;

                    this.payloadRows.set(payload.rows);
                    this.status.set(Statuses.Loaded);
                },
            },
        });
        //#endregion
    }

    headerClick(colKey: ColumnKey, col: HeaderCol) {
        this.headerClicked.emit({ colKey, col });
        this.activeHeaderCol.set({ colKey, col });
    }

    load() {
        this.loaded.emit(this.rows());
    }
}
